import { useState, useEffect } from 'react';

interface NavigationProps {
  scrolled: boolean;
}

export default function Navigation({ scrolled }: NavigationProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'About', href: '#about' },
    { label: 'Why Us', href: '#why-us' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      // Cleanup in case the component unmounts while the menu is open
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full mx-auto px-8 lg:px-12 py-4 flex items-center justify-between">
        {/* Logo / Brand */}
        <a href="#" className="flex items-center gap-3 cursor-pointer">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              scrolled ? 'bg-brand-500' : 'bg-white/20 backdrop-blur-sm'
            }`}
          >
            <i
              className={`ri-shield-cross-line text-xl ${
                scrolled ? 'text-white' : 'text-white'
              }`}
            ></i>
          </div>
          <div>
            {/* Replace nested <a> with a <span> to avoid invalid HTML */}
            <h1
              className={`text-base font-display font-bold leading-tight ${
                scrolled ? 'text-navy-900' : 'text-white'
              }`}
            >
              <span>Data Health</span>
            </h1>
            <span
              className={`text-[10px] tracking-wider uppercase font-medium ${
                scrolled ? 'text-brand-600' : 'text-brand-300'
              }`}
            >
              Insurance Consultants
            </span>
          </div>
        </a>

        {/* Desktop navigation links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`text-sm font-medium tracking-wide hover:text-brand-500 transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-navy-700' : 'text-white/90'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop actions */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:7138049039"
            className={`flex items-center gap-2 text-sm font-medium cursor-pointer whitespace-nowrap ${
              scrolled ? 'text-navy-800' : 'text-white'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-brand-500"></i>
            </div>
            (713) 804-9039
          </a>
          <a
            href="#contact"
            className="px-6 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Free Quote
          </a>
        </div>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMobileOpen((prev) => !prev)}
          className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          <i
            className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl ${
              scrolled ? 'text-navy-900' : 'text-white'
            }`}
          ></i>
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block text-navy-800 text-base font-medium py-2 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:7138049039"
              className="block text-brand-600 text-base font-semibold py-2 cursor-pointer"
            >
              Call (713) 804-9039
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
